import { useState, useMemo } from 'react';
import {
  AIWorkflow,
  WorkflowStepMapping,
  type WorkflowStep,
} from '@erp/shared-types';

export interface WorkflowStepFilterOption {
  value: WorkflowStep | 'all';
  label: string;
  color?: string;
}

// Centralized workflow labels and colors - used across the entire app
export const WORKFLOW_STEP_LABELS: Record<WorkflowStep, string> = {
  EXTRACT_SALES_ORDER: 'Sales Order Extraction',
  PRICE_SHEET_INGESTION: 'Price Sheet Extraction',
  AP_INVOICE_INGESTION: 'AP Invoice Extraction',
  SHORT_PAY_RECOMMENDATION: 'Short Pay Recommendation',
};

export const WORKFLOW_STEP_COLORS: Record<WorkflowStep, string> = {
  EXTRACT_SALES_ORDER: 'bg-blue-100 text-blue-700',
  PRICE_SHEET_INGESTION: 'bg-green-100 text-green-700',
  AP_INVOICE_INGESTION: 'bg-purple-100 text-purple-700',
  SHORT_PAY_RECOMMENDATION: 'bg-amber-100 text-amber-700',
};

// Filter options (excluding MANUAL_CREATION from filter)
export const WORKFLOW_STEP_FILTER_OPTIONS: WorkflowStepFilterOption[] = [
  { value: 'all', label: 'All Workflow Steps' },
  {
    value: 'EXTRACT_SALES_ORDER',
    label: WORKFLOW_STEP_LABELS.EXTRACT_SALES_ORDER,
    color: WORKFLOW_STEP_COLORS.EXTRACT_SALES_ORDER,
  },
  {
    value: 'PRICE_SHEET_INGESTION',
    label: WORKFLOW_STEP_LABELS.PRICE_SHEET_INGESTION,
    color: WORKFLOW_STEP_COLORS.PRICE_SHEET_INGESTION,
  },
  {
    value: 'AP_INVOICE_INGESTION',
    label: WORKFLOW_STEP_LABELS.AP_INVOICE_INGESTION,
    color: WORKFLOW_STEP_COLORS.AP_INVOICE_INGESTION,
  },
  {
    value: 'SHORT_PAY_RECOMMENDATION',
    label: WORKFLOW_STEP_LABELS.SHORT_PAY_RECOMMENDATION,
    color: WORKFLOW_STEP_COLORS.SHORT_PAY_RECOMMENDATION,
  },
];

export function useWorkflowStepFilter(filterByWorkflow?: AIWorkflow | 'all') {
  const [selectedWorkflowStep, setSelectedWorkflowStep] = useState<
    WorkflowStep | 'all'
  >('all');

  const filteredWorkflowStep = useMemo(() => {
    return selectedWorkflowStep === 'all' ? undefined : selectedWorkflowStep;
  }, [selectedWorkflowStep]);

  // Filter options based on selected workflow
  const filteredOptions = useMemo(() => {
    if (!filterByWorkflow || filterByWorkflow === 'all') {
      return WORKFLOW_STEP_FILTER_OPTIONS;
    }

    const allowedSteps = WorkflowStepMapping[filterByWorkflow].flat();
    return WORKFLOW_STEP_FILTER_OPTIONS.filter(
      (option) => option.value === 'all' || allowedSteps.includes(option.value),
    );
  }, [filterByWorkflow]);

  const selectedOption = useMemo(() => {
    return (
      filteredOptions.find((option) => option.value === selectedWorkflowStep) ||
      filteredOptions[0]
    );
  }, [selectedWorkflowStep, filteredOptions]);

  const resetFilter = () => {
    setSelectedWorkflowStep('all');
  };

  return {
    selectedWorkflowStep,
    setSelectedWorkflowStep,
    filteredWorkflowStep,
    selectedOption,
    resetFilter,
    options: filteredOptions,
  };
}
